"use client";

// ===========================================
// konakla - Auth Page (Giriş / Kayıt)
// Desktop: Ortalanmış kart (popup/modal görünümü)
// Mobile: Scroll'suz kompakt düzen
// ===========================================

import { Suspense, useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Mail, Loader2, ArrowLeft, AlertCircle, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from "@/components/ui/input-otp";
import { createClient, createOtpClient } from "@/lib/supabase/client";
import { authErrorMessage } from "@/lib/auth-errors";
import { safeInternalPath } from "@/lib/utils/safe-redirect";
import { toast } from "sonner";

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

// Masaüstü split görünümünün SOL paneli — list.jpg tam-bleed fotoğraf + üstünde
// SOLA YASLI marka içeriği. (Yalnız görsel/tanıtım; auth mantığıyla ilgisi yok.
// Mobilde gizli.)
function AuthBrandPanel() {
  const points = ["Komisyon yok", "Aracısız, şeffaf iletişim", "Onaylı ve belgeli evler"];
  return (
    <div className="relative hidden w-1/2 overflow-hidden lg:block">
      {/* Tam-bleed arka plan fotoğrafı — panel yalnız lg+'de görünür, priority YOK
          (mobilde boşuna preload olmasın) */}
      {/* object-position sağa kaydırıldı → görselin sağ tarafı (sandalyeler) görünür */}
      <Image
        src="/list.jpg"
        alt=""
        fill
        sizes="50vw"
        className="object-cover object-[75%_center]"
      />
      {/* Metin okunurluğu için degrade */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/30" />

      {/* İçerik — justify-between: logo ÜST, başlık logo ile maddelerin ORTASINDA,
          maddeler ALT bölgede (mb ile en alttan biraz yukarıda). */}
      <div className="absolute inset-0 flex flex-col justify-between px-12 py-10 xl:px-16">
        {/* Logo — sol üst köşe */}
        <Link href="/" className="inline-block">
          <Image
            src="/logo_beyaz.png"
            alt="konakla"
            width={300}
            height={90}
            className="-ml-2 h-16 w-auto"
            priority
          />
        </Link>

        {/* BÜYÜK başlık — logo ile maddelerin ortasında; her satır bir kelime öbeği */}
        <h2 className="text-5xl leading-tight font-bold text-white [text-shadow:0_2px_12px_rgba(0,0,0,0.4)] xl:text-6xl">
          <span className="block">Türkiye&apos;nin</span>
          <span className="block">kısa süreli</span>
          <span className="block">konaklama</span>
          <span className="block">platformu!</span>
        </h2>

        {/* 3 madde — alt bölgede ama en altta değil (mb ile yukarıda) */}
        <ul className="mb-6 space-y-4">
          {points.map((t) => (
            <li key={t} className="flex items-center gap-3 text-base font-medium text-white">
              <BadgeCheck className="h-6 w-6 shrink-0 text-white" />
              {t}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const RESEND_COOLDOWN = 60;

function AuthPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  // Açık yönlendirme koruması (O-2). Burada ESKİDEN HİÇ guard YOKTU: değer
  // doğrudan `router.push()`'a gidiyordu, yani `/auth?redirect=https://evil...`
  // OTP doğrulaması BAŞARILI olduktan hemen sonra kullanıcıyı dışarı atıyordu.
  const redirectTo = safeInternalPath(searchParams.get("redirect"));
  const errorParam = searchParams.get("error");

  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const otpInputRef = useRef<HTMLInputElement>(null);

  const supabase = createClient();
  const otpClient = createOtpClient();

  useEffect(() => {
    if (!errorParam) return;
    // `decodeURIComponent` BİLEREK KALDIRILDI: `searchParams.get()` değeri
    // zaten çözüyor, ikinci çözme bozuk yüzde dizilerinde URIError fırlatıyordu.
    // Çalıştırılabilir kanıt: `?error=100%25` → get() → "100%" →
    // decodeURIComponent("100%") → URIError. useEffect içinde yakalanmadığı için
    // giriş sayfası hata sınırına düşüyor ve kullanıcı GİRİŞ YAPAMIYORDU (O-44).
    setError(authErrorMessage(errorParam));
  }, [errorParam]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  useEffect(() => {
    if (emailSent && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [emailSent]);

  const handleGoogleSignIn = async () => {
    // KVKK/sözleşme onayı kontrolü — checkbox işaretsizse kullanıcı uyarılır
    if (!acceptedTerms) {
      toast.warning("Önce sözleşmeleri onaylayın", {
        description: "Devam etmek için aşağıda yer alan onay kutucuğunu işaretleyin.",
      });
      return;
    }

    if (!supabase) {
      setError("Supabase bağlantısı kurulamadı. Lütfen daha sonra tekrar deneyin.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          // `encodeURIComponent` ŞART: `redirectTo` içindeki `?` ve `&`
          // kodlanmazsa sorgu dizesini böler. Somut örnek: hedef
          // `/profile?tab=subscription` iken `tab=subscription` callback'e
          // AYRI bir parametre olarak gidiyor ve sessizce kayboluyordu.
          redirectTo: `${window.location.origin}/auth/callback?redirect=${encodeURIComponent(redirectTo)}`,
        },
      });

      if (error) throw error;
    } catch (err) {
      setError("Google ile giriş yapılırken bir hata oluştu.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const sendOtp = async () => {
    if (!email || !otpClient) {
      if (!otpClient) {
        setError("Supabase bağlantısı kurulamadı. Lütfen daha sonra tekrar deneyin.");
      }
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { error } = await otpClient.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
        },
      });

      if (error) throw error;
      setResendCountdown(RESEND_COOLDOWN);
      return true;
    } catch (err) {
      setError("E-posta gönderilirken bir hata oluştu. Lütfen tekrar deneyin.");
      console.error(err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    // KVKK/sözleşme onayı kontrolü — checkbox işaretsizse kullanıcı uyarılır
    if (!acceptedTerms) {
      toast.warning("Önce sözleşmeleri onaylayın", {
        description: "Devam etmek için aşağıda yer alan onay kutucuğunu işaretleyin.",
      });
      return;
    }
    const success = await sendOtp();
    if (success) {
      setEmailSent(true);
      setOtpCode("");
    }
  };

  const handleVerifyOtp = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!otpClient || !supabase || otpCode.length !== 6) return;

    setIsVerifying(true);
    setError(null);

    try {
      // signInWithOtp ile gönderilen email OTP kodları `type: "email"` ile doğrulanır.
      // Hem mevcut kullanıcı girişi hem yeni kayıt için aynı tip geçerli.
      const { data, error: verifyError } = await otpClient.auth.verifyOtp({
        email,
        token: otpCode,
        type: "email",
      });

      if (verifyError) throw verifyError;
      const session = data.session;

      if (session) {
        await supabase.auth.setSession({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
        });

        // Welcome maili — server idempotency guard'ı ile korunuyor.
        // Mevcut kullanıcılarda skip edilir, yeni kayıtta bir kez gönderilir.
        fetch("/api/auth/welcome-email", { method: "POST" }).catch((err) => {
          console.warn("[auth] Welcome email isteği başarısız:", err);
        });
      }

      router.push(redirectTo);
      router.refresh();
    } catch (err) {
      setError("Doğrulama kodu hatalı veya süresi dolmuş. Lütfen tekrar deneyin.");
      setOtpCode("");
      console.error(err);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCountdown > 0) return;
    await sendOtp();
  };

  const handleBack = () => {
    setEmailSent(false);
    setOtpCode("");
    setError(null);
  };

  // OTP doğrulama ekranı
  if (emailSent) {
    return (
      <div className="fixed inset-0 z-50 flex bg-white">
        <AuthBrandPanel />

        <div className="flex w-full items-start justify-center overflow-y-auto p-4 lg:w-1/2 lg:items-center lg:p-6">
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 lg:max-w-lg lg:p-8">
            <button
              onClick={handleBack}
              className="text-muted-foreground hover:text-foreground mb-4 flex items-center gap-1.5 text-sm transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Geri dön
            </button>

            <div className="text-center">
              <div className="bg-primary/10 mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full">
                <Mail className="text-primary h-7 w-7" />
              </div>
              <h1 className="text-xl font-bold lg:text-3xl">Doğrulama kodunu girin</h1>
              <p className="text-muted-foreground mt-2 text-sm lg:text-base">
                <span className="text-foreground font-medium">{email}</span> adresine 6 haneli bir
                kod gönderdik.
              </p>
            </div>

            {error && (
              <div className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>{error}</p>
              </div>
            )}

            <form onSubmit={handleVerifyOtp} className="mt-6 space-y-4">
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={otpCode}
                  onChange={(value) => setOtpCode(value)}
                  autoFocus
                >
                  <InputOTPGroup>
                    <InputOTPSlot
                      index={0}
                      className="h-11 w-10 text-base font-bold lg:h-14 lg:w-12"
                    />
                    <InputOTPSlot
                      index={1}
                      className="h-11 w-10 text-base font-bold lg:h-14 lg:w-12"
                    />
                    <InputOTPSlot
                      index={2}
                      className="h-11 w-10 text-base font-bold lg:h-14 lg:w-12"
                    />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot
                      index={3}
                      className="h-11 w-10 text-base font-bold lg:h-14 lg:w-12"
                    />
                    <InputOTPSlot
                      index={4}
                      className="h-11 w-10 text-base font-bold lg:h-14 lg:w-12"
                    />
                    <InputOTPSlot
                      index={5}
                      className="h-11 w-10 text-base font-bold lg:h-14 lg:w-12"
                    />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button
                type="submit"
                className="h-11 w-full rounded-xl text-base font-medium lg:h-14"
                disabled={isVerifying || otpCode.length !== 6}
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Doğrulanıyor...
                  </>
                ) : (
                  "Doğrula"
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              {resendCountdown > 0 ? (
                <p className="text-muted-foreground text-sm">
                  Tekrar göndermek için{" "}
                  <span className="text-foreground font-medium">{resendCountdown}s</span> bekleyin
                </p>
              ) : (
                <button
                  onClick={handleResendOtp}
                  disabled={isLoading}
                  className="text-primary text-sm font-medium hover:underline disabled:opacity-50"
                >
                  {isLoading ? "Gönderiliyor..." : "Kodu tekrar gönder"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex bg-white">
      <AuthBrandPanel />

      <div className="flex w-full items-center justify-center overflow-y-auto p-4 lg:w-1/2 lg:p-6">
        <div className="relative w-full max-w-md rounded-2xl bg-white lg:max-h-[92vh] lg:max-w-lg lg:overflow-y-auto">
          <div className="p-6 pt-8 lg:p-8">
            {/* Mobil logo — masaüstünde sol panel logosu var, tekrarı gizle */}
            <div className="flex justify-center lg:hidden">
              <Link href="/">
                <Image
                  src="/logo_mavi.png"
                  alt="konakla logosu"
                  width={240}
                  height={72}
                  className="h-18 w-auto"
                  priority
                />
              </Link>
            </div>

            <div className="mt-5 text-center">
              <h1 className="text-xl font-bold lg:text-3xl">Hoş Geldiniz!</h1>
              <p className="text-muted-foreground mt-1 text-sm">
                Giriş yapın veya ücretsiz hesap oluşturun
              </p>
            </div>

            {error && (
              <div className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>{error}</p>
              </div>
            )}

            <div className="mt-5 lg:mt-6">
              <Button
                variant="outline"
                className="h-11 w-full gap-3 rounded-xl border-2 text-base font-medium lg:h-14"
                onClick={handleGoogleSignIn}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <GoogleIcon className="h-5 w-5" />
                )}
                Google ile devam et
              </Button>
            </div>

            <div className="my-4 flex items-center gap-3 lg:my-5">
              <div className="bg-border h-px flex-1" />
              <span className="text-muted-foreground text-xs lg:text-sm">veya e-posta ile</span>
              <div className="bg-border h-px flex-1" />
            </div>

            <form onSubmit={handleEmailSignIn} className="space-y-3">
              <div className="relative">
                <Mail className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 lg:h-5 lg:w-5" />
                <Input
                  id="email"
                  type="email"
                  placeholder="E-posta adresinizi girin"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 pl-10 text-base lg:h-14"
                  required
                />
              </div>
              <Button
                type="submit"
                className="h-11 w-full rounded-xl text-base font-medium lg:h-14"
                disabled={isLoading || !email}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Gönderiliyor...
                  </>
                ) : (
                  "Doğrulama kodu gönder"
                )}
              </Button>
            </form>

            <label
              htmlFor="accept-terms-auth"
              className="mt-4 flex cursor-pointer items-start gap-2.5 select-none"
            >
              <Checkbox
                id="accept-terms-auth"
                checked={acceptedTerms}
                onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
                className="mt-0.5"
              />
              <span className="text-muted-foreground text-xs leading-relaxed">
                <Link
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline-offset-2 hover:underline"
                >
                  Kullanım Koşulları
                </Link>
                {"'nı, "}
                <Link
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline-offset-2 hover:underline"
                >
                  Gizlilik Politikası
                </Link>
                {"'nı ve "}
                <Link
                  href="/kvkk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline-offset-2 hover:underline"
                >
                  KVKK Aydınlatma Metni
                </Link>
                {"'ni okudum, anladım ve kabul ediyorum."}
              </span>
            </label>

            <p className="text-muted-foreground mt-3 text-center text-xs lg:text-sm">
              E-postanıza gönderilen 6 haneli kodu girerek giriş yapabilirsiniz.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * `useSearchParams()` kullanan istemci sayfaları, kök app/loading.tsx kaldırıldığı
 * için KENDİ Suspense sınırlarını taşımak zorunda. Sınır olmadan Next.js derleme
 * sırasında prerender hatası verir (build yeşil olmadan deploy başlamaz).
 *
 * Fallback bilinçli olarak eski kök iskeletle AYNI görünüyor — kullanıcı açısından
 * hiçbir davranış değişmesin diye.
 */
function SayfaYukleniyor() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="text-primary h-10 w-10 animate-spin" />
        <p className="text-muted-foreground text-lg font-medium">Yükleniyor...</p>
      </div>
    </div>
  );
}

export default function AuthPage() {
  return (
    <Suspense fallback={<SayfaYukleniyor />}>
      <AuthPageContent />
    </Suspense>
  );
}
